import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import {
  Boxes,
  Download,
  FileText,
  Printer,
  Search,
  RefreshCw,
  PackageOpen,
  History,
  Send,
  Wifi,
  WifiOff,
  FileBadge,
  Filter,
  ArrowLeftRight,
  PackageCheck,
  Check,
  ChevronsUpDown,
  Scale,
  AlertTriangle,
  Trash2,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function ProductCodeCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState([]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/pallets/product-codes`, getAuthHeaders())
      .then((r) => { if (alive) setCodes(r.data?.codes || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="max-w-[200px] w-[200px] justify-between font-normal"
          data-testid="product-filter"
        >
          <span className={`truncate ${value ? "text-zinc-900 font-mono text-xs" : "text-zinc-400"}`}>
            {value || "Product code…"}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search code…" data-testid="product-filter-search" />
          <CommandList>
            <CommandEmpty>No matching product code.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => { onChange(""); setOpen(false); }}
                data-testid="product-filter-all"
              >
                <Check className={`mr-2 h-4 w-4 ${value ? "opacity-0" : "opacity-100"}`} />
                All product codes
              </CommandItem>
              {codes.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => { onChange(c === value ? "" : c); setOpen(false); }}
                  data-testid={`product-filter-option-${c}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c ? "opacity-100" : "opacity-0"}`} />
                  <span className="font-mono text-xs">{c}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


const STATE_COLORS = {
  PRINTED: "bg-zinc-100 text-zinc-700",
  ASSIGNED: "bg-blue-100 text-blue-800",
  PRODUCED: "bg-amber-100 text-amber-800",
  PACKED: "bg-green-100 text-green-800",
  IN_CONTAINER: "bg-violet-100 text-violet-800",
  DISPATCHED: "bg-emerald-100 text-emerald-800",
  DELIVERED: "bg-teal-100 text-teal-800",
  REWORK: "bg-orange-100 text-orange-800",
  SCRAPPED: "bg-red-100 text-red-800",
  VOIDED: "bg-zinc-100 text-zinc-500 line-through",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}

export default function Pallets({ user, onLogout }) {
  const [permissions, setPermissions] = useState([]);
  const [activeTab, setActiveTab] = useState("pallets");
  const [batches, setBatches] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [loadingPallets, setLoadingPallets] = useState(true);
  const [loadingBatches, setLoadingBatches] = useState(true);

  const [stateFilter, setStateFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [search, setSearch] = useState("");
  const [poFilter, setPoFilter] = useState("");
  const [producedFrom, setProducedFrom] = useState("");
  const [producedTo, setProducedTo] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTo, setUpdatedTo] = useState("");
  const [cardStatusFilter, setCardStatusFilter] = useState("");
  const [legacyFilter, setLegacyFilter] = useState(""); // "" | "only" | "exclude"
  const [dprFilter, setDprFilter] = useState(""); // "" | "on" | "off"
  const [reportIdFilter, setReportIdFilter] = useState(""); // exact DPR id
  const [reportDateFilter, setReportDateFilter] = useState(""); // display label

  const [printDialog, setPrintDialog] = useState({ open: false });
  const [printCount, setPrintCount] = useState(50);
  const [printing, setPrinting] = useState(false);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [directPrintingId, setDirectPrintingId] = useState(null);

  // Legacy (pre-QR) stock pallet minting — bulk multi-row
  const [legacyDialog, setLegacyDialog] = useState({ open: false });
  const [legRows, setLegRows] = useState([]);
  const [legNote, setLegNote] = useState("");
  const [creatingLegacy, setCreatingLegacy] = useState(false);

  const [detailPallet, setDetailPallet] = useState(null);

  // ---- Allocation & Reassignment (Phase 5) ----
  const [reassign, setReassign] = useState({ open: false, pallet: null });
  const [reassignPos, setReassignPos] = useState([]);
  const [reassignLoadingPos, setReassignLoadingPos] = useState(false);
  const [reassignPoId, setReassignPoId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const [unassignedGroups, setUnassignedGroups] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  const has = (p) => permissions.includes(p);
  const canView = has("pallet.view");
  const canPrint = has("pallet.print");
  const canPack = has("pallet.pack");
  const canAssign = has("pallet.assign_po");
  const canReassign = has("pallet.reassign_po");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
        setPermissions(r.data.effective_permissions || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    if (!canView && permissions.length > 0) {
      setLoadingPallets(false);
      setLoadingBatches(false);
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.length, stateFilter, productFilter, search, poFilter, producedFrom, producedTo, updatedFrom, updatedTo, cardStatusFilter, legacyFilter, dprFilter, reportIdFilter]);

  useEffect(() => {
    if (activeTab === "unassigned" && canView) {
      fetchUnassigned();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAll = async () => {
    await Promise.all([fetchPallets(), fetchBatches(), fetchPrinterStatus()]);
  };

  const fetchPrinterStatus = async () => {
    try {
      const r = await axios.get(`${API}/pallet/printer/status`, getAuthHeaders());
      setPrinterStatus(r.data);
    } catch {
      setPrinterStatus(null);
    }
  };

  const downloadBridgeScript = async () => {
    try {
      const res = await axios.get(`${API}/pallet/printer/bridge-script`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "tsc_print_bridge.py";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Bridge script downloaded — open it in a text editor for install steps");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const handleDirectPrint = async (batch) => {
    setDirectPrintingId(batch.id);
    try {
      const r = await axios.post(`${API}/pallet/stickers/batches/${batch.id}/print-direct`, {}, getAuthHeaders());
      toast.success(`Sent ${r.data.bytes_sent} bytes to ${r.data.host}:${r.data.port} — ${r.data.labels_queued} labels queued`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Direct print failed");
    } finally {
      setDirectPrintingId(null);
    }
  };

  const fetchPallets = async () => {
    setLoadingPallets(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.append("state", stateFilter);
      if (productFilter) params.append("product_code", productFilter);
      if (search) params.append("search", search);
      if (poFilter) params.append("po_no", poFilter);
      if (producedFrom) params.append("produced_from", producedFrom);
      if (producedTo) params.append("produced_to", producedTo);
      if (updatedFrom) params.append("updated_from", updatedFrom);
      if (updatedTo) params.append("updated_to", updatedTo);
      if (cardStatusFilter) params.append("card_status", cardStatusFilter);
      if (legacyFilter) params.append("legacy", legacyFilter);
      if (reportIdFilter) {
        params.append("report_id", reportIdFilter);
      } else if (dprFilter === "on") {
        params.append("attached_to_report", "true");
      } else if (dprFilter === "off") {
        params.append("unattached_to_report", "true");
      }
      params.append("limit", "500");
      const r = await axios.get(`${API}/pallets?${params.toString()}`, getAuthHeaders());
      setPallets(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load pallets");
    } finally {
      setLoadingPallets(false);
    }
  };

  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const r = await axios.get(`${API}/pallet/stickers/batches`, getAuthHeaders());
      setBatches(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load batches");
    } finally {
      setLoadingBatches(false);
    }
  };

  const handlePrintBatch = async () => {
    const n = parseInt(printCount, 10);
    if (!n || n < 1 || n > 5000) {
      toast.error("Enter a count between 1 and 5000");
      return;
    }
    setPrinting(true);
    try {
      const r = await axios.post(`${API}/pallet/stickers/print`, { count: n }, getAuthHeaders());
      toast.success(`Batch ${r.data.batch_no} created — ${r.data.count} pallet IDs (${r.data.id_range_start} → ${r.data.id_range_end})`);
      setPrintDialog({ open: false });
      setPrintCount(50);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to print batch");
    } finally {
      setPrinting(false);
    }
  };

  const emptyLegRow = () => ({ product_code: "", po_id: "", qty_per_pallet: "", pallet_count: "", default_qty: null, pos: [], looked_up: false, loading: false });

  const openLegacyDialog = () => {
    setLegRows([emptyLegRow()]);
    setLegNote("");
    setLegacyDialog({ open: true });
  };

  const addLegRow = () => setLegRows((rows) => [...rows, emptyLegRow()]);
  const removeLegRow = (idx) => setLegRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)));
  const updateLegRow = (idx, patch) => setLegRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const lookupLegRow = async (idx) => {
    const row = legRows[idx];
    const code = (row?.product_code || "").trim();
    if (!code) { toast.error("Enter a product code first"); return; }
    updateLegRow(idx, { loading: true });
    try {
      const [prodR, poR] = await Promise.all([
        axios.get(`${API}/products/by-code/${encodeURIComponent(code)}`, getAuthHeaders()),
        axios.get(`${API}/pallet/po-picker?product_code=${encodeURIComponent(code)}`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      const dq = prodR?.data?.default_qty_per_pallet ?? null;
      const pos = Array.isArray(poR?.data) ? poR.data : (poR?.data?.items || []);
      updateLegRow(idx, {
        loading: false, looked_up: true, default_qty: dq, pos,
        product_code: prodR.data.product_code,
        qty_per_pallet: row.qty_per_pallet || (dq ? String(dq) : ""),
      });
    } catch (err) {
      updateLegRow(idx, { loading: false, looked_up: false });
      toast.error(err.response?.data?.detail || `Product "${code}" not found`);
    }
  };

  const legTotals = () => {
    let ready = 0, pallets = 0, pieces = 0, pending = 0;
    for (const r of legRows) {
      const qty = parseInt(r.qty_per_pallet, 10) || 0;
      const cnt = parseInt(r.pallet_count, 10) || 0;
      if (r.looked_up && qty > 0 && cnt > 0) { ready += 1; pallets += cnt; pieces += qty * cnt; }
      else if (r.product_code.trim() && !r.looked_up) pending += 1;
    }
    return { ready, pallets, pieces, pending };
  };

  const createLegacyPallets = async () => {
    const rows = legRows
      .filter((r) => r.looked_up && r.product_code.trim())
      .map((r) => ({
        product_code: r.product_code.trim(),
        po_id: r.po_id || undefined,
        qty_per_pallet: parseInt(r.qty_per_pallet, 10),
        pallet_count: parseInt(r.pallet_count, 10),
      }));
    if (!rows.length) { toast.error("Add at least one looked-up product row"); return; }
    for (const r of rows) {
      if (!r.qty_per_pallet || r.qty_per_pallet < 1) { toast.error(`${r.product_code}: pieces/pallet must be > 0`); return; }
      if (!r.pallet_count || r.pallet_count < 1) { toast.error(`${r.product_code}: number of pallets must be > 0`); return; }
    }
    setCreatingLegacy(true);
    try {
      const r = await axios.post(`${API}/pallets/legacy/bulk`, { rows, note: legNote || null }, getAuthHeaders());
      toast.success(`Minted ${r.data.count} legacy pallets across ${r.data.rows.length} product${r.data.rows.length > 1 ? "s" : ""} (${r.data.range.start} → ${r.data.range.end})`, { duration: 6000 });
      setLegacyDialog({ open: false });
      await fetchAll();
      if (r.data.pallet_ids?.length) {
        await bulkPrintCards(r.data.pallet_ids);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mint legacy pallets");
    } finally {
      setCreatingLegacy(false);
    }
  };

  const downloadBatchFile = async (batch, kind) => {
    try {
      const res = await axios.get(`${API}/pallet/stickers/batches/${batch.id}/${kind}`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${batch.batch_no}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const openPalletCard = async (palletId) => {
    try {
      // Append a timestamp so the browser / any CDN can never serve a stale
      // copy of the card (the layout & content change with pallet state and
      // backend rollouts).
      const res = await axios.get(`${API}/pallet/${palletId}/card.pdf?t=${Date.now()}`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open card");
    }
  };

  // ---- Bulk pallet-card printing + mark packed ----
  const bulkPrintCards = async (ids) => {
    if (!ids.length) return { ok: false };
    try {
      const res = await axios.post(
        `${API}/pallets/cards.pdf`,
        { pallet_ids: ids },
        { ...getAuthHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      const rendered = res.headers["x-cards-rendered"];
      const missing = res.headers["x-cards-missing"];
      toast.success(`Opened ${rendered || ids.length} pallet card(s) for printing`);
      if (missing) toast.message(`Skipped (not found): ${missing}`);
      return { ok: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate cards");
      return { ok: false };
    }
  };

  const bulkMarkPacked = async (ids) => {
    if (!ids.length) return { ok: false, packed: 0 };
    try {
      const r = await axios.post(`${API}/pallets/mark-packed`, { pallet_ids: ids }, getAuthHeaders());
      const d = r.data || {};
      if (d.packed_count) toast.success(`${d.packed_count} pallet(s) marked Packed`);
      if (d.skipped_count) toast.message(`${d.skipped_count} skipped (already packed / not produced)`);
      await fetchPallets();
      return { ok: true, packed: d.packed_count };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mark packed failed");
      return { ok: false };
    }
  };

  // ---- Reassignment handlers ----
  const openReassign = async (pallet) => {
    setReassign({ open: true, pallet });
    setReassignPoId(pallet.po_id || "");
    setReassignReason("");
    setReassignPos([]);
    if (pallet.product_code) {
      setReassignLoadingPos(true);
      try {
        const r = await axios.get(
          `${API}/pallet/po-picker?product_code=${encodeURIComponent(pallet.product_code)}`,
          getAuthHeaders()
        );
        setReassignPos(r.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load POs");
      } finally {
        setReassignLoadingPos(false);
      }
    }
  };

  const submitReassign = async () => {
    if (!reassign.pallet) return;
    if (!reassignReason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setReassigning(true);
    try {
      const r = await axios.post(
        `${API}/pallet/${reassign.pallet.id}/reassign-po`,
        { po_id: reassignPoId || null, reason: reassignReason.trim() },
        getAuthHeaders()
      );
      toast.success(reassignPoId ? `Reassigned to PO ${r.data.po_no}` : "Pallet unassigned");
      setReassign({ open: false, pallet: null });
      setDetailPallet(r.data); // refresh open detail with updated doc
      await Promise.all([fetchPallets(), fetchUnassigned()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reassignment failed");
    } finally {
      setReassigning(false);
    }
  };

  const fetchUnassigned = async () => {
    if (!canView) return;
    setLoadingUnassigned(true);
    try {
      const r = await axios.get(`${API}/pallets/unassigned`, getAuthHeaders());
      setUnassignedGroups(r.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load unassigned pallets");
    } finally {
      setLoadingUnassigned(false);
    }
  };

  const submitBulkAssign = async (poId, palletIds, reason) => {
    const r = await axios.post(
      `${API}/pallets/bulk-assign-po`,
      { po_id: poId, pallet_ids: palletIds, reason },
      getAuthHeaders()
    );
    const { assigned_count, skipped_count } = r.data;
    if (assigned_count > 0) toast.success(`Assigned ${assigned_count} pallet(s) to PO ${r.data.po_number}`);
    if (skipped_count > 0) toast.warning(`${skipped_count} pallet(s) skipped`);
    await Promise.all([fetchPallets(), fetchUnassigned()]);
    return r.data;
  };

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Pallets" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
        { label: "Invoice cum Packing Lists", to: "/safety/packing-lists", depth: 1 },
        { label: "Pallets", active: true, depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="pallets-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Boxes className="w-5 h-5 text-orange-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Pallet Production Tracking</h1>
              <p className="text-xs text-zinc-500">
                Scan-driven production reporting. Print sticker batches, monitor pallet lifecycle from PRINTED → DISPATCHED.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canPrint && (
              <Button onClick={() => setPrintDialog({ open: true })} data-testid="print-batch-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print Sticker Batch
              </Button>
            )}
            {canPrint && (
              <Button variant="outline" onClick={openLegacyDialog} data-testid="legacy-stock-btn">
                <Boxes className="w-4 h-4 mr-2" />
                Legacy Stock
              </Button>
            )}
            <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!canView && permissions.length > 0 && (
          <div className="sf-surface p-8 text-center text-zinc-500 text-sm">
            You do not have permission to view pallets.
          </div>
        )}

        {canView && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="pallets" data-testid="tab-pallets">
                <PackageOpen className="w-4 h-4 mr-2" />
                Pallets ({pallets.length})
              </TabsTrigger>
              <TabsTrigger value="batches" data-testid="tab-batches">
                <Printer className="w-4 h-4 mr-2" />
                Sticker Batches ({batches.length})
              </TabsTrigger>
              {(canAssign || canReassign) && (
                <TabsTrigger value="unassigned" data-testid="tab-unassigned">
                  <PackageCheck className="w-4 h-4 mr-2" />
                  Unassigned
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="pallets" className="mt-4">
              <PalletList
                pallets={pallets}
                loading={loadingPallets}
                stateFilter={stateFilter}
                setStateFilter={setStateFilter}
                productFilter={productFilter}
                setProductFilter={setProductFilter}
                search={search}
                setSearch={setSearch}
                poFilter={poFilter}
                setPoFilter={setPoFilter}
                producedFrom={producedFrom}
                setProducedFrom={setProducedFrom}
                producedTo={producedTo}
                setProducedTo={setProducedTo}
                updatedFrom={updatedFrom}
                setUpdatedFrom={setUpdatedFrom}
                updatedTo={updatedTo}
                setUpdatedTo={setUpdatedTo}
                cardStatusFilter={cardStatusFilter}
                setCardStatusFilter={setCardStatusFilter}
                legacyFilter={legacyFilter}
                setLegacyFilter={setLegacyFilter}
                dprFilter={dprFilter}
                setDprFilter={setDprFilter}
                reportIdFilter={reportIdFilter}
                setReportIdFilter={setReportIdFilter}
                reportDateFilter={reportDateFilter}
                setReportDateFilter={setReportDateFilter}
                onOpen={(p) => setDetailPallet(p)}
                onOpenCard={openPalletCard}
                canPrint={canPrint}
                canPack={canPack}
                onBulkPrint={bulkPrintCards}
                onBulkPack={bulkMarkPacked}
              />
            </TabsContent>

            <TabsContent value="batches" className="mt-4">
              <BatchList
                batches={batches}
                loading={loadingBatches}
                onDownload={downloadBatchFile}
                printerStatus={printerStatus}
                onDirectPrint={handleDirectPrint}
                directPrintingId={directPrintingId}
                onDownloadBridge={downloadBridgeScript}
              />
            </TabsContent>

            {(canAssign || canReassign) && (
              <TabsContent value="unassigned" className="mt-4">
                <AllocationView
                  groups={unassignedGroups}
                  loading={loadingUnassigned}
                  canAssign={canAssign}
                  onRefresh={fetchUnassigned}
                  onBulkAssign={submitBulkAssign}
                />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>

      {/* Legacy stock dialog (bulk multi-row) */}
      <Dialog open={legacyDialog.open} onOpenChange={(o) => !o && !creatingLegacy && setLegacyDialog({ open: false })}>
        <DialogContent className="max-w-3xl" data-testid="legacy-dialog">
          <DialogHeader>
            <DialogTitle>Add Legacy (Pre-QR) Stock Pallets</DialogTitle>
            <DialogDescription>
              Add one row per product. Each row mints that many pallet cards for old stock — date-stamped just before the active cutover, so they never count as new production but can be scanned during a physical stock count. Cards print together as a single PDF. Requires an active Cutover baseline.
            </DialogDescription>
          </DialogHeader>

          <div className="py-1">
            {/* Column headers */}
            <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.9fr_1.4fr_32px] gap-2 px-1 pb-1 text-[10.5px] font-medium text-zinc-500 uppercase tracking-wide">
              <div>Product code</div>
              <div className="text-right">Pcs / pallet</div>
              <div className="text-right"># Pallets</div>
              <div className="text-right">Total pcs</div>
              <div>Assign PO (optional)</div>
              <div></div>
            </div>

            <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1" data-testid="legacy-rows">
              {legRows.map((row, idx) => {
                const qty = parseInt(row.qty_per_pallet, 10) || 0;
                const cnt = parseInt(row.pallet_count, 10) || 0;
                const rowTotal = qty * cnt;
                return (
                  <div key={idx} className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.9fr_1.4fr_32px] gap-2 items-start" data-testid={`legacy-row-${idx}`}>
                    <div>
                      <Input
                        value={row.product_code}
                        onChange={(e) => updateLegRow(idx, { product_code: e.target.value, looked_up: false })}
                        onBlur={() => { if (row.product_code.trim() && !row.looked_up) lookupLegRow(idx); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupLegRow(idx); } }}
                        placeholder="e.g. AA-45"
                        className={`h-9 font-mono ${row.looked_up ? "border-emerald-300 bg-emerald-50/40" : row.product_code.trim() ? "border-amber-300 bg-amber-50/40" : ""}`}
                        data-testid={`legacy-code-${idx}`}
                      />
                      {row.loading ? (
                        <div className="text-[10.5px] text-zinc-400 mt-0.5 pl-0.5">Looking up…</div>
                      ) : row.looked_up ? (
                        <div className="text-[10.5px] text-emerald-600 mt-0.5 pl-0.5" data-testid={`legacy-ok-${idx}`}>
                          ✓ found{row.default_qty ? ` · default ${row.default_qty}/pallet` : " · no default qty"}
                        </div>
                      ) : row.product_code.trim() ? (
                        <div className="text-[10.5px] text-amber-600 mt-0.5 pl-0.5">↵ press Enter to look up</div>
                      ) : null}
                    </div>
                    <Input
                      type="number" min={1}
                      value={row.qty_per_pallet}
                      onChange={(e) => updateLegRow(idx, { qty_per_pallet: e.target.value })}
                      placeholder={row.default_qty ? String(row.default_qty) : "—"}
                      className="h-9 text-right tabular-nums"
                      disabled={!row.looked_up}
                      data-testid={`legacy-qty-${idx}`}
                    />
                    <Input
                      type="number" min={1} max={2000}
                      value={row.pallet_count}
                      onChange={(e) => updateLegRow(idx, { pallet_count: e.target.value })}
                      placeholder="—"
                      className="h-9 text-right tabular-nums"
                      disabled={!row.looked_up}
                      data-testid={`legacy-count-${idx}`}
                    />
                    <div className="h-9 flex items-center justify-end text-sm tabular-nums text-zinc-700" data-testid={`legacy-rowtotal-${idx}`}>
                      {rowTotal ? rowTotal.toLocaleString() : <span className="text-zinc-300">—</span>}
                    </div>
                    <select
                      className="h-9 w-full border rounded px-2 text-sm bg-white disabled:bg-zinc-50 disabled:text-zinc-400"
                      value={row.po_id}
                      onChange={(e) => updateLegRow(idx, { po_id: e.target.value })}
                      disabled={!row.looked_up}
                      data-testid={`legacy-po-${idx}`}
                    >
                      <option value="">{row.looked_up ? "Unassigned" : "Look up first"}</option>
                      {row.pos.map((po) => (
                        <option key={po.po_id} value={po.po_id}>
                          {po.po_number} — {po.customer_name || "?"} (pending {po.pending_qty})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeLegRow(idx)}
                      disabled={legRows.length <= 1}
                      className="h-9 w-8 flex items-center justify-center rounded text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      title="Remove row"
                      data-testid={`legacy-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              onClick={addLegRow}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
              data-testid="legacy-add-row"
            >
              <Plus className="w-4 h-4" />
              Add product row
            </button>

            <div className="mt-4">
              <Label className="text-xs text-zinc-500">Note for all rows (optional)</Label>
              <Input
                value={legNote}
                onChange={(e) => setLegNote(e.target.value)}
                placeholder="e.g. Old rack stock, batch May-2024"
                data-testid="legacy-note"
              />
            </div>

            {(() => {
              const t = legTotals();
              return (
                <div className="mt-4 flex items-center gap-3 flex-wrap rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-2.5 text-sm" data-testid="legacy-batch-total">
                  <span className="font-semibold text-zinc-900">Batch total</span>
                  <span className="text-zinc-600">{t.ready} product{t.ready === 1 ? "" : "s"} ready</span>
                  <span className="text-zinc-300">·</span>
                  <span className="text-zinc-600"><span className="font-semibold text-zinc-900">{t.pallets.toLocaleString()}</span> pallets</span>
                  <span className="text-zinc-300">·</span>
                  <span className="text-zinc-600"><span className="font-semibold text-zinc-900">{t.pieces.toLocaleString()}</span> pieces</span>
                  {t.pending > 0 && <span className="ml-auto text-[11px] text-amber-600">{t.pending} row{t.pending === 1 ? "" : "s"} need look-up</span>}
                </div>
              );
            })()}
            <p className="text-[11px] text-zinc-500 mt-2">
              IDs use a reserved legacy series (PLT-YYYY-9xxxxx). Unassigned pallets can be assigned to a PO later from the list. After minting, all cards open as one PDF to print.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLegacyDialog({ open: false })} disabled={creatingLegacy}>
              Cancel
            </Button>
            <Button onClick={createLegacyPallets} disabled={creatingLegacy || legTotals().ready === 0} data-testid="legacy-create-confirm">
              {creatingLegacy ? "Minting…" : `Mint ${legTotals().pallets || ""} pallets & print all cards`.replace("  ", " ")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print dialog */}
      <Dialog open={printDialog.open} onOpenChange={(o) => !o && setPrintDialog({ open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Print Pallet Sticker Batch</DialogTitle>
            <DialogDescription>
              ERP reserves a range of unique pallet IDs (PLT-YYYY-NNNNNN) and generates a PDF (universal) plus a TSPL2 .prn file for direct printing on the TSC TE244.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-500">How many pallet IDs to generate?</Label>
              <Input
                type="number"
                min={1}
                max={5000}
                value={printCount}
                onChange={(e) => setPrintCount(e.target.value)}
                data-testid="print-count-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Each sticker is 100 × 100 mm. One sticker per pallet.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintDialog({ open: false })}>
              Cancel
            </Button>
            <Button onClick={handlePrintBatch} disabled={printing} data-testid="print-batch-confirm">
              {printing ? "Generating…" : "Generate Batch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pallet detail */}
      <Dialog open={!!detailPallet} onOpenChange={(o) => !o && setDetailPallet(null)}>
        <DialogContent className="max-w-2xl">
          {detailPallet && (
            <PalletDetail
              pallet={detailPallet}
              canReassign={canReassign}
              onReassign={openReassign}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign / Change PO dialog */}
      <ReassignDialog
        state={reassign}
        onClose={() => setReassign({ open: false, pallet: null })}
        pos={reassignPos}
        loadingPos={reassignLoadingPos}
        poId={reassignPoId}
        setPoId={setReassignPoId}
        reason={reassignReason}
        setReason={setReassignReason}
        submitting={reassigning}
        onSubmit={submitReassign}
      />
    </SafetyShell>
  );
}

function PalletList({ pallets, loading, stateFilter, setStateFilter, productFilter, setProductFilter, search, setSearch, poFilter, setPoFilter, producedFrom, setProducedFrom, producedTo, setProducedTo, updatedFrom, setUpdatedFrom, updatedTo, setUpdatedTo, cardStatusFilter, setCardStatusFilter, legacyFilter, setLegacyFilter, dprFilter, setDprFilter, reportIdFilter, setReportIdFilter, reportDateFilter, setReportDateFilter, onOpen, onOpenCard, canPrint, canPack, onBulkPrint, onBulkPack }) {
  const [selected, setSelected] = useState({}); // pallet_id -> pallet
  const [markAfterPrint, setMarkAfterPrint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [containerPayload, setContainerPayload] = useState(26000); // kg — editable max container payload

  const isPrintable = (p) => ["PRODUCED", "PACKED"].includes(p.state);
  const printableList = pallets.filter(isPrintable);

  const [unreported, setUnreported] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pallets/unreported-count`, getAuthHeaders())
      .then((r) => setUnreported(r.data))
      .catch(() => {});
  }, [pallets.length]);

  const selectedIds = Object.keys(selected);
  const selectedProducedIds = selectedIds.filter((id) => selected[id]?.state === "PRODUCED");
  const allSelected = printableList.length > 0 && printableList.every((p) => selected[p.id]);

  // Container/shipment weight estimate for the current selection
  const selectedWeightKg = selectedIds.reduce((sum, id) => sum + (selected[id]?.net_weight_kg || 0), 0);
  const selectedUnweighed = selectedIds.filter((id) => selected[id]?.net_weight_kg == null).length;
  const payloadNum = Number(containerPayload) || 0;
  const payloadPct = payloadNum > 0 ? (selectedWeightKg / payloadNum) * 100 : 0;
  const overPayload = payloadNum > 0 && selectedWeightKg > payloadNum;

  // Footer: net weight of ALL currently-filtered pallets
  const filteredWeightKg = pallets.reduce((sum, p) => sum + (p.net_weight_kg || 0), 0);
  const filteredUnweighed = pallets.filter((p) => p.net_weight_kg == null).length;

  const toggle = (p) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = p;
      return next;
    });

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next = {};
      printableList.forEach((p) => (next[p.id] = p));
      setSelected(next);
    }
  };

  const doPrint = async () => {
    setBusy(true);
    const res = await onBulkPrint(selectedIds);
    if (res?.ok && markAfterPrint && canPack && selectedProducedIds.length) {
      await onBulkPack(selectedProducedIds);
    }
    setBusy(false);
    setSelected({});
    setMarkAfterPrint(false);
  };

  const doPack = async () => {
    if (!selectedProducedIds.length) return;
    setBusy(true);
    await onBulkPack(selectedProducedIds);
    setBusy(false);
    setSelected({});
  };

  return (
    <div className="sf-surface p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pallet ID…"
            className="pl-9"
            data-testid="pallet-search"
          />
        </div>
        <select
          className="border rounded h-9 px-2 text-sm bg-white"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          data-testid="state-filter"
        >
          <option value="">All states</option>
          {Object.keys(STATE_COLORS).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <ProductCodeCombobox value={productFilter} onChange={setProductFilter} />
        <select
          className="border rounded h-9 px-2 text-sm bg-white"
          value={reportIdFilter ? "report" : dprFilter}
          onChange={(e) => {
            const v = e.target.value;
            if (v !== "report") { setReportIdFilter(""); setReportDateFilter(""); }
            setDprFilter(v === "report" ? "" : v);
          }}
          data-testid="dpr-filter"
        >
          <option value="">DPR: Any</option>
          <option value="on">On a DPR</option>
          <option value="off">Not on a DPR</option>
          {reportIdFilter && <option value="report">DPR: {reportDateFilter || reportIdFilter}</option>}
        </select>
        {reportIdFilter && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-2 py-1"
            data-testid="dpr-report-chip"
          >
            <FileBadge className="w-3 h-3" />
            DPR {reportDateFilter || reportIdFilter}
            <button
              className="ml-0.5 hover:text-emerald-900"
              onClick={() => { setReportIdFilter(""); setReportDateFilter(""); }}
              data-testid="dpr-report-chip-clear"
              title="Clear DPR filter"
            >
              ×
            </button>
          </span>
        )}
        <span className="text-xs text-zinc-500 ml-auto">{pallets.length} pallets</span>
        {unreported && unreported.count > 0 && (
          <button
            onClick={() => { setReportIdFilter(""); setReportDateFilter(""); setDprFilter("off"); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs px-2.5 py-1 hover:bg-amber-100 transition-colors"
            title="These PRODUCED/PACKED pallets are not on any Daily Production Report yet — click to view them"
            data-testid="unreported-nudge"
          >
            <AlertTriangle className="w-3 h-3" />
            {unreported.count} not on a DPR
            <span className="text-amber-600">· {unreported.pieces.toLocaleString()} pcs</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap text-sm">
        <Input
          value={poFilter}
          onChange={(e) => setPoFilter(e.target.value)}
          placeholder="PO number(s)…"
          className="max-w-[180px]"
          title="Filter by PO number — comma-separate for multiple"
          data-testid="po-filter"
        />
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          Produced
          <Input
            type="date"
            value={producedFrom}
            onChange={(e) => setProducedFrom(e.target.value)}
            className="h-9 w-[150px]"
            data-testid="produced-from-filter"
          />
          <span>→</span>
          <Input
            type="date"
            value={producedTo}
            onChange={(e) => setProducedTo(e.target.value)}
            className="h-9 w-[150px]"
            data-testid="produced-to-filter"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          Updated
          <Input
            type="date"
            value={updatedFrom}
            onChange={(e) => setUpdatedFrom(e.target.value)}
            className="h-9 w-[150px]"
            data-testid="updated-from-filter"
          />
          <span>→</span>
          <Input
            type="date"
            value={updatedTo}
            onChange={(e) => setUpdatedTo(e.target.value)}
            className="h-9 w-[150px]"
            data-testid="updated-to-filter"
          />
        </label>
        <select
          className="border rounded h-9 px-2 text-sm bg-white"
          value={cardStatusFilter}
          onChange={(e) => setCardStatusFilter(e.target.value)}
          data-testid="card-status-filter"
        >
          <option value="">Card: any</option>
          <option value="not_printed">Card not printed</option>
          <option value="printed">Card printed</option>
        </select>
        <select
          className="border rounded h-9 px-2 text-sm bg-white"
          value={legacyFilter}
          onChange={(e) => setLegacyFilter(e.target.value)}
          data-testid="legacy-filter"
          title="Filter legacy (pre-QR) stock pallets"
        >
          <option value="">Legacy: any</option>
          <option value="only">Legacy only</option>
          <option value="exclude">Hide legacy</option>
        </select>
        {(poFilter || producedFrom || producedTo || updatedFrom || updatedTo || cardStatusFilter || legacyFilter) && (
          <button
            className="text-xs text-zinc-500 hover:text-zinc-800 underline"
            onClick={() => { setPoFilter(""); setProducedFrom(""); setProducedTo(""); setUpdatedFrom(""); setUpdatedTo(""); setCardStatusFilter(""); setLegacyFilter(""); }}
            data-testid="clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {canPrint && selectedIds.length > 0 && (
        <div className="mb-3 flex items-center gap-3 flex-wrap rounded-lg border border-orange-200 bg-orange-50 px-3 py-2" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-orange-900" data-testid="bulk-selected-count">
            {selectedIds.length} selected
          </span>
          <Button size="sm" onClick={doPrint} disabled={busy} data-testid="bulk-print-cards-btn">
            <FileBadge className="w-4 h-4 mr-1.5" />
            {busy ? "Working…" : `Print ${selectedIds.length} Card${selectedIds.length > 1 ? "s" : ""}`}
          </Button>
          {canPack && (
            <Button
              size="sm"
              variant="outline"
              onClick={doPack}
              disabled={busy || selectedProducedIds.length === 0}
              title={selectedProducedIds.length === 0 ? "No PRODUCED pallets selected" : "Mark selected PRODUCED pallets as Packed"}
              data-testid="bulk-mark-packed-btn"
            >
              <PackageCheck className="w-4 h-4 mr-1.5" />
              Mark Packed ({selectedProducedIds.length})
            </Button>
          )}
          {canPack && (
            <label className="flex items-center gap-1.5 text-xs text-orange-900 cursor-pointer select-none" data-testid="bulk-mark-after-print">
              <input
                type="checkbox"
                checked={markAfterPrint}
                onChange={(e) => setMarkAfterPrint(e.target.checked)}
              />
              Mark as Packed after printing
            </label>
          )}
          <button
            className="text-xs text-zinc-500 hover:text-zinc-800 ml-auto"
            onClick={() => setSelected({})}
            data-testid="bulk-clear-selection"
          >
            Clear
          </button>
          {/* Container weight estimator */}
          <div className="w-full mt-1 pt-2 border-t border-orange-200 flex items-center gap-3 flex-wrap" data-testid="bulk-weight-estimator">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-900">
              <Scale className="w-4 h-4" />
              Net weight:
              <span className="font-mono tabular-nums" data-testid="bulk-selected-weight">
                {selectedWeightKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
              </span>
            </span>
            {selectedUnweighed > 0 && (
              <span className="text-xs text-zinc-500" data-testid="bulk-weight-unweighed">
                ({selectedUnweighed} pallet{selectedUnweighed > 1 ? "s" : ""} missing weight — not counted)
              </span>
            )}
            <label className="flex items-center gap-1.5 text-xs text-orange-900">
              Container max
              <Input
                type="number"
                value={containerPayload}
                onChange={(e) => setContainerPayload(e.target.value)}
                className="h-7 w-24 text-xs"
                data-testid="bulk-container-payload-input"
              />
              kg
            </label>
            <div className="flex items-center gap-2 min-w-[160px] flex-1 max-w-xs">
              <div className="h-2 flex-1 rounded-full bg-orange-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${overPayload ? "bg-red-500" : payloadPct > 85 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(payloadPct, 100)}%` }}
                  data-testid="bulk-payload-bar"
                />
              </div>
              <span className={`text-xs font-medium tabular-nums ${overPayload ? "text-red-600" : "text-zinc-600"}`} data-testid="bulk-payload-pct">
                {payloadPct.toFixed(0)}%
              </span>
            </div>
            {overPayload && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600" data-testid="bulk-payload-warning">
                <AlertTriangle className="w-3.5 h-3.5" />
                Over by {(selectedWeightKg - payloadNum).toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
              </span>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded border bg-white">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto" />
          </div>
        ) : pallets.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">
            <PackageOpen className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">No pallets match these filters</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                {canPrint && (
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      title="Select all printable (PRODUCED/PACKED) pallets"
                      data-testid="bulk-select-all"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left font-medium">Pallet ID</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">Net wt</th>
                <th className="px-3 py-2 text-left font-medium">PO</th>
                <th className="px-3 py-2 text-left font-medium">Customer</th>
                <th className="px-3 py-2 text-left font-medium">DPR</th>
                <th className="px-3 py-2 text-left font-medium">Produced</th>
                <th className="px-3 py-2 text-left font-medium">Updated</th>
                <th className="px-3 py-2 text-right font-medium w-24">Card</th>
              </tr>
            </thead>
            <tbody>
              {pallets.map((p) => (
                <tr
                  key={p.id}
                  className="border-t hover:bg-orange-50/40 cursor-pointer"
                  onClick={() => onOpen(p)}
                  data-testid={`pallet-row-${p.id}`}
                >
                  {canPrint && (
                    <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                      {isPrintable(p) && (
                        <input
                          type="checkbox"
                          checked={!!selected[p.id]}
                          onChange={() => toggle(p)}
                          data-testid={`pallet-check-${p.id}`}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono text-xs">{p.pallet_no}</td>
                  <td className="px-3 py-2">
                    <Badge className={STATE_COLORS[p.state] || ""} variant="secondary">
                      {p.state}
                    </Badge>
                    {p.legacy && (
                      <span
                        className="ml-1 inline-block rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 align-middle"
                        title={p.legacy_note ? `Legacy (pre-QR) stock — ${p.legacy_note}` : "Legacy (pre-QR) stock — physical handle for stock counting, excluded from production"}
                        data-testid={`pallet-legacy-badge-${p.id}`}
                      >
                        LEGACY
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{p.product_code || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {p.actual_qty || p.target_qty || "—"}
                    {p.qty_overridden && (
                      <span
                        className="ml-1 inline-block rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 align-middle"
                        title={p.qty_override_reason ? `Custom qty — ${p.qty_override_reason}` : `Custom qty (default ${p.default_qty_per_pallet ?? "?"})`}
                        data-testid={`pallet-custom-qty-${p.id}`}
                      >
                        CUSTOM
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums" data-testid={`pallet-weight-${p.id}`}>
                    {p.net_weight_kg != null
                      ? <span className="text-violet-700 font-medium">{p.net_weight_kg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</span>
                      : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2">{p.po_no || <span className="text-zinc-400">Unassigned</span>}</td>
                  <td className="px-3 py-2 text-xs">{p.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                    {p.report_id ? (
                      <Link
                        to={`/safety/production-reports/${p.report_id}`}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                        title={`Attached to DPR ${p.report_date || ""} (${p.report_status || ""})`}
                        data-testid={`pallet-dpr-link-${p.id}`}
                      >
                        <FileBadge className="w-3 h-3" />
                        {p.report_date || "View"}
                      </Link>
                    ) : (
                      <span className="text-zinc-300" data-testid={`pallet-dpr-none-${p.id}`}>—</span>
                    )}
                    {p.report_id && (
                      <button
                        className="ml-1.5 text-zinc-400 hover:text-emerald-700 align-middle"
                        onClick={() => { setReportIdFilter(p.report_id); setReportDateFilter(p.report_date || ""); }}
                        title="Show all pallets on this DPR"
                        data-testid={`pallet-dpr-filter-${p.id}`}
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500" data-testid={`pallet-produced-${p.id}`} title={p.production_date ? `DPR date: ${p.production_date}` : ""}>{fmtDate(p.paired_at)}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{fmtDate(p.updated_at)}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {["PRODUCED", "PACKED"].includes(p.state) && onOpenCard && (
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onOpenCard(p.id)}
                          data-testid={`pallet-card-${p.id}`}
                          title="Open A4 pallet card PDF"
                        >
                          <FileBadge className="w-3 h-3 mr-1" />
                          Card
                        </Button>
                        {p.card_printed_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-emerald-700"
                            title={`Card printed ${fmtDate(p.card_printed_at)}${p.card_print_count ? ` · ${p.card_print_count}×` : ""}`}
                            data-testid={`card-printed-${p.id}`}
                          >
                            <Check className="w-3 h-3" />
                            {fmtDate(p.card_printed_at)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-zinc-400" data-testid={`card-unprinted-${p.id}`}>
                            not printed
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-zinc-50 border-t-2 border-zinc-300 font-medium">
              <tr>
                {canPrint && <td className="px-3 py-2"></td>}
                <td className="px-3 py-2 text-zinc-600" colSpan={3}>
                  Totals · {pallets.length} pallet{pallets.length === 1 ? "" : "s"}
                </td>
                <td className="px-3 py-2 text-right text-zinc-600">
                  {pallets.reduce((s, p) => s + (p.actual_qty || p.target_qty || 0), 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right text-violet-700 tabular-nums" data-testid="pallet-weight-total">
                  {filteredWeightKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                  {filteredUnweighed > 0 && (
                    <span className="block text-[10px] font-normal text-zinc-400" data-testid="pallet-weight-total-unweighed">
                      {filteredUnweighed} no wt
                    </span>
                  )}
                </td>
                <td className="px-3 py-2" colSpan={6}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

function BatchList({ batches, loading, onDownload, printerStatus, onDirectPrint, directPrintingId, onDownloadBridge }) {
  const directPrintEnabled = !!(printerStatus && printerStatus.configured);
  return (
    <div className="sf-surface p-4">
      {/* Printer status pill + bridge-script link */}
      <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
        {printerStatus?.reachable ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
            <Wifi className="w-3.5 h-3.5" />
            TSC printer online · {printerStatus.host}:{printerStatus.port}
          </span>
        ) : printerStatus?.configured ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="printer-status-unreachable">
            <WifiOff className="w-3.5 h-3.5" />
            Printer configured but unreachable · {printerStatus.host}:{printerStatus.port}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 border" data-testid="printer-status-unconfigured">
            <WifiOff className="w-3.5 h-3.5" />
            Direct printing not configured — set TSC_PRINTER_HOST in backend .env
          </span>
        )}
        {onDownloadBridge && (
          <Button size="sm" variant="ghost" onClick={onDownloadBridge} data-testid="download-bridge-btn">
            <Download className="w-3 h-3 mr-1" />
            Download USB→TCP bridge script
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded border bg-white">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto" />
          </div>
        ) : batches.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">
            <Printer className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">No sticker batches yet</p>
            <p className="text-xs">Click "Print Sticker Batch" to generate your first batch</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Batch #</th>
                <th className="px-3 py-2 text-left font-medium">Range</th>
                <th className="px-3 py-2 text-right font-medium">Count</th>
                <th className="px-3 py-2 text-right font-medium">Claimed</th>
                <th className="px-3 py-2 text-left font-medium">Printed By</th>
                <th className="px-3 py-2 text-left font-medium">Printed At</th>
                <th className="px-3 py-2 text-right font-medium w-60">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-t" data-testid={`batch-row-${b.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{b.batch_no}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {b.id_range_start} → {b.id_range_end}
                  </td>
                  <td className="px-3 py-2 text-right">{b.count}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs">
                      {b.claimed_count}/{b.count}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{b.printed_by}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{fmtDate(b.printed_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {directPrintEnabled && (
                      <Button
                        size="sm"
                        className="mr-1"
                        onClick={() => onDirectPrint(b)}
                        disabled={directPrintingId === b.id}
                        data-testid={`direct-print-${b.id}`}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        {directPrintingId === b.id ? "Sending…" : "Print to TSC"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="mr-1"
                      onClick={() => onDownload(b, "csv")}
                      data-testid={`download-csv-${b.id}`}
                      title="CSV for BarTender database printing"
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mr-1"
                      onClick={() => onDownload(b, "pdf")}
                      data-testid={`download-pdf-${b.id}`}
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDownload(b, "prn")}
                      data-testid={`download-prn-${b.id}`}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      PRN
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-zinc-500 mt-3">
        <strong>Print to TSC</strong> sends the TSPL2 .prn directly to your printer over TCP 9100 (set <code className="text-[10px] bg-zinc-100 px-1 rounded">TSC_PRINTER_HOST</code> in backend .env).
        <strong> PDF</strong> works on any printer (set page size to 100×100 mm in driver).
        <strong> PRN</strong> is a downloadable TSPL2 file you can drop into a TSC driver queue manually.
      </p>
    </div>
  );
}

function PalletDetail({ pallet, canReassign, onReassign }) {
  const reassignable = ["ASSIGNED", "PRODUCED", "PACKED"].includes(pallet.state);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-mono">{pallet.pallet_no}</DialogTitle>
        <DialogDescription>
          <Badge className={STATE_COLORS[pallet.state] || ""} variant="secondary">
            {pallet.state}
          </Badge>
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Product" value={pallet.product_code} mono />
          <Field label="Target / Actual Qty" value={`${pallet.target_qty ?? "—"} / ${pallet.actual_qty ?? "—"}`} />
          {pallet.qty_overridden && (
            <Field
              label="Custom Qty"
              value={`${pallet.qty_override_reason || "—"}${pallet.default_qty_per_pallet ? ` (default ${pallet.default_qty_per_pallet})` : ""}`}
            />
          )}
          <Field label="PO" value={pallet.po_no || "Unassigned"} />
          <Field label="Customer" value={pallet.customer_name} />
          <Field label="Sticker Batch" value={pallet.sticker_batch_no} mono />
          <Field label="Paired by" value={pallet.paired_by_email} small />
        </div>

        {canReassign && (
          <div className="flex items-center justify-between rounded border border-orange-200 bg-orange-50 px-3 py-2">
            <div className="text-xs text-orange-800">
              {pallet.po_no ? (
                <>Currently on PO <span className="font-mono font-semibold">{pallet.po_no}</span></>
              ) : (
                <>This pallet is <span className="font-semibold">unassigned</span></>
              )}
            </div>
            {reassignable ? (
              <Button
                size="sm"
                onClick={() => onReassign(pallet)}
                data-testid="change-po-btn"
              >
                <ArrowLeftRight className="w-3 h-3 mr-1" />
                Change PO
              </Button>
            ) : (
              <span className="text-xs text-zinc-500">Locked ({pallet.state})</span>
            )}
          </div>
        )}

        <div>
          <div className="text-xs font-medium text-zinc-600 mb-1 flex items-center gap-1">
            <History className="w-3 h-3" />
            Events
          </div>
          <div className="border rounded bg-zinc-50 p-2 max-h-40 overflow-auto text-xs font-mono">
            {(pallet.events || []).map((e, i) => (
              <div key={i} className="py-0.5">
                <span className="text-orange-700">{e.state}</span>
                <span className="text-zinc-400"> · </span>
                {fmtDate(e.at)}
                <span className="text-zinc-400"> · </span>
                {e.by}
              </div>
            ))}
          </div>
        </div>
        {(pallet.allocation_history || []).length > 0 && (
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-1">PO Allocation History</div>
            <div className="border rounded bg-zinc-50 p-2 max-h-40 overflow-auto text-xs" data-testid="allocation-history">
              {pallet.allocation_history.map((h, i) => (
                <div key={i} className="py-1 border-b last:border-0">
                  <div className="font-mono">
                    {h.from_po_no || h.from_po_id || "—"}{" "}
                    <span className="text-zinc-400">→</span>{" "}
                    <span className="font-semibold">{h.to_po_no || (h.to_po_id ? h.to_po_id : "Unassigned")}</span>
                  </div>
                  <div className="text-zinc-500">
                    {h.context} · {h.by_user_email} · {fmtDate(h.at)}
                  </div>
                  {h.reason && <div className="text-zinc-600 italic">“{h.reason}”</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ReassignDialog({ state, onClose, pos, loadingPos, poId, setPoId, reason, setReason, submitting, onSubmit }) {
  const pallet = state.pallet;
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change PO</DialogTitle>
          <DialogDescription>
            {pallet && (
              <>
                Reassign pallet <span className="font-mono">{pallet.pallet_no}</span>{" "}
                (<span className="font-mono">{pallet.product_code}</span>) to a different PO,
                or leave it unassigned. The change is audit-logged.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-500">Target PO</Label>
            <select
              className="border rounded h-9 px-2 text-sm bg-white w-full mt-1"
              value={poId}
              onChange={(e) => setPoId(e.target.value)}
              data-testid="reassign-po-select"
            >
              <option value="">— Unassigned —</option>
              {pos.map((p) => (
                <option key={p.po_id} value={p.po_id}>
                  {p.po_number} · {p.customer_name || "—"} · pending {p.pending_qty}
                </option>
              ))}
            </select>
            {loadingPos && <p className="text-xs text-zinc-400 mt-1">Loading POs…</p>}
            {!loadingPos && pos.length === 0 && (
              <p className="text-xs text-zinc-400 mt-1">
                No open POs with pending qty for this product. You can still unassign.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Reason (required)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. wrong PO chosen on shift"
              className="mt-1"
              data-testid="reassign-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={submitting || !reason.trim()} data-testid="reassign-submit">
            {submitting ? "Saving…" : "Apply Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllocationView({ groups, loading, canAssign, onRefresh, onBulkAssign }) {
  const [selected, setSelected] = useState({}); // pallet_id -> product_code
  const [assignDialog, setAssignDialog] = useState({ open: false, productCode: null });
  const [assignPos, setAssignPos] = useState([]);
  const [assignPoId, setAssignPoId] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [assigning, setAssigning] = useState(false);

  const selectedIds = Object.keys(selected);
  // pallets can only be bulk-assigned within a single product (a PO must contain it)
  const selectedProducts = [...new Set(Object.values(selected))];

  const toggle = (palletId, productCode) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[palletId]) delete next[palletId];
      else next[palletId] = productCode;
      return next;
    });
  };

  const openAssign = async () => {
    if (selectedProducts.length !== 1) {
      toast.error("Select pallets of a single product to assign to one PO");
      return;
    }
    const productCode = selectedProducts[0];
    setAssignDialog({ open: true, productCode });
    setAssignPoId("");
    setAssignReason("");
    setAssignPos([]);
    try {
      const r = await axios.get(
        `${API}/pallet/po-picker?product_code=${encodeURIComponent(productCode)}`,
        getAuthHeaders()
      );
      setAssignPos(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load POs");
    }
  };

  const submitAssign = async () => {
    if (!assignPoId) {
      toast.error("Pick a PO");
      return;
    }
    if (!assignReason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setAssigning(true);
    try {
      await onBulkAssign(assignPoId, selectedIds, assignReason.trim());
      setAssignDialog({ open: false, productCode: null });
      setSelected({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk assign failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="sf-surface p-4" data-testid="allocation-view">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <PackageCheck className="w-4 h-4 text-orange-600" />
            Unassigned Inventory
          </h2>
          <p className="text-xs text-zinc-500">
            Live pallets (ASSIGNED / PRODUCED / PACKED) with no PO. Select pallets of one product and assign them to an open PO.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canAssign && (
            <Button
              size="sm"
              onClick={openAssign}
              disabled={selectedIds.length === 0}
              data-testid="bulk-assign-btn"
            >
              <ArrowLeftRight className="w-3 h-3 mr-1" />
              Assign to PO ({selectedIds.length})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onRefresh} data-testid="unassigned-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {selectedProducts.length > 1 && (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          You selected pallets across {selectedProducts.length} products. A PO assignment must be one product at a time.
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto" />
        </div>
      ) : groups.length === 0 ? (
        <div className="p-10 text-center text-zinc-500">
          <PackageCheck className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
          <p className="text-sm">No unassigned pallets — everything is allocated.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.product_code} className="border rounded bg-white overflow-hidden">
              <div className="bg-zinc-50 px-3 py-2 flex items-center justify-between">
                <span className="font-mono text-sm font-semibold">{g.product_code}</span>
                <span className="text-xs text-zinc-500">
                  {g.count} pallet(s) · {g.pieces} pcs
                </span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {g.pallets.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-orange-50/40" data-testid={`unassigned-row-${p.id}`}>
                      <td className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={!!selected[p.id]}
                          onChange={() => toggle(p.id, g.product_code)}
                          data-testid={`unassigned-check-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.pallet_no}</td>
                      <td className="px-3 py-2">
                        <Badge className={STATE_COLORS[p.state] || ""} variant="secondary">
                          {p.state}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">{p.actual_qty || p.target_qty || "—"}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500">{fmtDate(p.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Bulk assign dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => !o && setAssignDialog({ open: false, productCode: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {selectedIds.length} pallet(s) to a PO</DialogTitle>
            <DialogDescription>
              Product <span className="font-mono">{assignDialog.productCode}</span>. Only POs that contain this product are shown.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-500">Target PO</Label>
              <select
                className="border rounded h-9 px-2 text-sm bg-white w-full mt-1"
                value={assignPoId}
                onChange={(e) => setAssignPoId(e.target.value)}
                data-testid="bulk-assign-po-select"
              >
                <option value="">— Select PO —</option>
                {assignPos.map((p) => (
                  <option key={p.po_id} value={p.po_id}>
                    {p.po_number} · {p.customer_name || "—"} · pending {p.pending_qty}
                  </option>
                ))}
              </select>
              {assignPos.length === 0 && (
                <p className="text-xs text-zinc-400 mt-1">No open POs with pending qty for this product.</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Reason (required)</Label>
              <Input
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="e.g. allocate finished stock to PO"
                className="mt-1"
                data-testid="bulk-assign-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, productCode: null })}>
              Cancel
            </Button>
            <Button onClick={submitAssign} disabled={assigning || !assignPoId || !assignReason.trim()} data-testid="bulk-assign-submit">
              {assigning ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono, small }) {
  return (
    <div>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${small ? "text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
